import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { LineReveal, FadeIn, Reveal } from "@/components/Reveal";

const PRINCIPLES = [
  { n: "01", t: "Done it first", d: "We only onboard people who built a career in the work itself — performing at a real, professional level before teaching a single class.", tone: "emerald" },
  { n: "02", t: "Checked, then shown", d: "That track record is verified before onboarding, and it stays visible on every course page. Trust you can read, not assume.", tone: "terracotta" },
  { n: "03", t: "Any serious skill", d: "Technical, creative, business, everyday life skills — if learning from someone who's actually done it matters, it belongs here.", tone: "surface" },
  { n: "04", t: "A conversation, not a sale", d: "Fees are starting figures. You enquire; we confirm the real numbers and dates with you. No urgency tricks, no pressure.", tone: "surface" },
];

const toneCls = {
  emerald: "bg-emerald text-white",
  terracotta: "bg-terracotta text-white",
  surface: "bg-surface border border-line text-ink shadow-sm",
};
const numCls = { emerald: "text-lemon", terracotta: "text-white/40", surface: "text-terracotta" };

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="mesh-wash relative overflow-hidden">
        <div className="blob-decoration w-[460px] h-[460px] bg-violetlight -top-40 -right-32" />
        <div className="w-full mx-auto px-4 md:px-8 lg:px-12 pt-10 md:pt-14 pb-10 md:pb-14 relative">
          <FadeIn>
            <p className="font-sans text-xs font-bold text-terracotta mb-8 uppercase tracking-wide">About Fluenex Academy</p>
          </FadeIn>
          <h1 className="font-display font-bold tracking-tight text-5xl md:text-7xl leading-[1.0] max-w-5xl text-ink">
            <LineReveal lines={["Real access to", "people who have"]} />
            <span className="text-emerald block"><LineReveal lines={["actually done it."]} delay={0.28} /></span>
          </h1>
          <FadeIn delay={0.6}>
            <p className="font-sans text-lg md:text-2xl text-graphite mt-8 max-w-3xl leading-relaxed">
              Too many people have tried to learn something real and kept running into
              teachers who don't actually know the work. Fluenex exists for exactly them.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <span className="w-14 h-14 rounded-2xl bg-terracotta text-white flex items-center justify-center mb-6"><ShieldCheck size={26} /></span>
              <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl leading-[1.02] text-ink">One promise, on every page.</h2>
              <p className="text-graphite mt-6 leading-relaxed max-w-sm text-lg">
                A coding course and a dance class are treated identically here. The subject
                changes; the standard for who teaches it never does.
              </p>
            </div>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.06}>
                <div className={`h-full rounded-3xl p-8 ${toneCls[p.tone]}`}>
                  <span className={`font-display font-bold text-6xl leading-none ${numCls[p.tone]}`}>{p.n}</span>
                  <h3 className="font-display font-semibold text-2xl mt-5">{p.t}</h3>
                  <p className={`mt-3 leading-relaxed ${p.tone === "surface" ? "text-graphite" : "text-white/85"}`}>{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full mx-auto px-4 md:px-8 lg:px-12 pb-24 md:pb-32">
        <Reveal>
          <div className="bg-stone rounded-3xl px-8 md:px-16 py-10 md:py-14">
            <h2 className="font-display font-bold tracking-tight text-4xl md:text-6xl leading-[1.0] max-w-4xl text-ink">Start with the catalog.</h2>
            <Link to="/courses" data-testid="about-cta" className="mt-6 group inline-flex items-center gap-2.5 rounded-full bg-terracotta text-white font-bold text-base px-8 py-4 shadow-md hover:bg-terracottahover hover:shadow-glowwarm transition duration-300">
              See every course <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
